import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useDropzone } from 'react-dropzone';
import FilterSelect from 'components/Form/filterSelect';
import useUploadHook from 'components/uploadHook';
import { toast, Bounce } from 'react-toastify';
import { createUniformItemApi, updateUniformItemApi } from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import { uniformGenderOptions } from '../../constants';
import FormModalShell, { StatusToggleRow } from 'components/modelpopup/FormModalShell';

const schema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string(),
  image: Yup.string(),
  gender: Yup.object().nullable().required('Gender is required'),
  color: Yup.string(),
  rotation_cycle_months: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  status: Yup.boolean()
});

const genderOptions = uniformGenderOptions.map(o => ({ label: o.label, value: o.value }));

const Field = ({ label, required, error, children }) => (
  <div className="mb-3">
    <label
      style={{
        display: 'block',
        fontSize: 12.5,
        fontWeight: 600,
        color: '#374151',
        marginBottom: 6,
        letterSpacing: '0.01em',
      }}
    >
      {label}
      {required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
    </label>
    {children}
    {error && (
      <small style={{ color: '#dc2626', fontSize: 12, marginTop: 4, display: 'block' }}>
        {error}
      </small>
    )}
  </div>
);

const UniformItemFormModal = ({ open, onClose, selectedItem, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const { sizeOptions } = useUniformMgmt();
  const { uploadLoading, handleFile } = useUploadHook();

  const { control, handleSubmit, formState: { errors }, setValue, reset, setError, register, watch } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '', description: '', image: '', gender: null,
      color: '', rotation_cycle_months: '', status: true, sizes: []
    }
  });

  useEffect(() => {
    if (open) {
      if (selectedItem) {
        setValue('name', selectedItem.name);
        setValue('description', selectedItem.description || '');
        setValue('image', selectedItem.image || '');
        setValue('color', selectedItem.color || '');
        setValue('rotation_cycle_months', selectedItem.rotation_cycle_months || '');
        setValue('status', selectedItem.status);
        if (selectedItem.gender) {
          setValue('gender', genderOptions.find(o => o.value === selectedItem.gender));
        }
        if (selectedItem.sizes) {
          setValue('sizes', sizeOptions.filter(opt =>
            selectedItem.sizes.some(s => s.id === opt.value)
          ));
        }
      } else {
        reset({ name: '', description: '', image: '', gender: null, color: '', rotation_cycle_months: '', status: true, sizes: [] });
      }
    }
  }, [selectedItem, open]);

  const fileUploadHandler = async (acceptedFiles, rejectedFiles, e) => {
    const urls = await handleFile(e);
    const url = Array.isArray(urls) ? urls[0] : urls;
    if (url) setValue('image', url);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: fileUploadHandler,
    accept: { 'image/*': [] },
    multiple: false,
  });

  const imageUrl = watch('image');

  const onSubmit = (data) => {
    setLoading(true);
    const payload = {
      name: data.name,
      description: data.description || '',
      image: data.image || '',
      gender: data.gender?.value,
      color: data.color || '',
      rotation_cycle_months: data.rotation_cycle_months || null,
      size_ids: data.sizes?.map(s => s.value) || [],
      status: data.status
    };

    const apiCall = selectedItem
      ? updateUniformItemApi(selectedItem.id, payload)
      : createUniformItemApi(payload);

    apiCall.then(() => {
      toast.success(selectedItem ? 'Item updated!' : 'Item created!', {
        position: "top-right", autoClose: 3000, transition: Bounce
      });
      setLoading(false);
      reset();
      onSuccess();
    }).catch(e => {
      setLoading(false);
      try {
        if (e.response?.data) {
          Object.entries(e.response.data).forEach(([k, v]) => {
            setError(k, { type: 'manual', message: Array.isArray(v) ? v.join(", ") : v });
          });
        }
      } catch {
        toast.error('Operation failed', { position: "top-right", autoClose: 3000, transition: Bounce });
      }
    });
  };

  return (
    <FormModalShell
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      title={selectedItem ? "Edit Uniform Item" : "Add Uniform Item"}
      subtitle="Define the item, sizes available, and replacement cycle."
      submitLabel={selectedItem ? "Update" : "Create"}
      loading={loading}
      disabled={uploadLoading}
      width={780}
    >
      <div className="row g-3">
        <div className="col-md-6">
          <Field label="Name" required error={errors.name?.message}>
            <Controller name="name" control={control}
              render={({ field }) => <input className="form-control" {...field} placeholder="Uniform Name" />}
            />
          </Field>
        </div>
        <div className="col-md-6">
          <Field label="Description" error={errors.description?.message}>
            <Controller name="description" control={control}
              render={({ field }) => <input className="form-control" {...field} placeholder="Short description" />}
            />
          </Field>
        </div>

        <div className="col-md-6">
          <Field label="Gender" required error={errors.gender?.message}>
            <Controller name="gender" control={control}
              render={({ field: { value, onChange } }) => (
                <FilterSelect
                  isClearable={true}
                  onChange={onChange}
                  optionLabel="label"
                  optionValue="value"
                  options={genderOptions}
                  isLoading={false}
                  value={value}
                  placeholder="Select Gender"
                />
              )}
            />
          </Field>
        </div>
        <div className="col-md-6">
          <Field label="Color" error={errors.color?.message}>
            <Controller name="color" control={control}
              render={({ field }) => <input className="form-control" {...field} placeholder="e.g. Red" />}
            />
          </Field>
        </div>

        <div className="col-md-6">
          <Field label="Sizes">
            <Controller name="sizes" control={control}
              render={({ field: { value, onChange } }) => (
                <FilterSelect
                  {...register("sizes")}
                  isClearable={true}
                  isMulti
                  onChange={onChange}
                  optionLabel="label"
                  optionValue="value"
                  options={sizeOptions}
                  isLoading={false}
                  value={value}
                  placeholder="Select Sizes"
                />
              )}
            />
          </Field>
        </div>
        <div className="col-md-6">
          <Field
            label="Rotation Cycle (months)"
            error={errors.rotation_cycle_months?.message}
          >
            <Controller name="rotation_cycle_months" control={control}
              render={({ field }) => (
                <input className="form-control" {...field} type="number" placeholder="e.g. 6" min="0" />
              )}
            />
          </Field>
        </div>

        <div className="col-12">
          <Field
            label={`Image${uploadLoading ? "  (uploading…)" : ""}`}
            error={errors.image?.message}
          >
            <div
              {...getRootProps({ className: 'dropzone' })}
              style={{
                border: `1.5px dashed ${isDragActive ? '#dc2626' : '#d1d5db'}`,
                background: isDragActive ? '#fef2f2' : '#fafbfc',
                padding: imageUrl ? '12px' : '24px',
                textAlign: 'center',
                cursor: 'pointer',
                borderRadius: 10,
                transition: 'all 0.15s ease',
              }}
            >
              <input {...getInputProps()} />
              {imageUrl ? (
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <img
                      src={imageUrl}
                      alt="uniform"
                      style={{
                        width: 64, height: 64, objectFit: 'cover',
                        borderRadius: 8, border: '1px solid #e5e7eb',
                      }}
                    />
                    <div className="text-start">
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                        Image uploaded
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        Click or drop to replace
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger"
                    onClick={(e) => { e.stopPropagation(); setValue('image', ''); }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div>
                  <i className="la la-cloud-upload-alt"
                    style={{ fontSize: 28, color: '#9ca3af', display: 'block', marginBottom: 6 }} />
                  <div style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>
                    Drop an image here or click to browse
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                    PNG, JPG up to a few MB
                  </div>
                </div>
              )}
            </div>
          </Field>
        </div>

        <div className="col-12">
          <Controller name="status" control={control}
            render={({ field: { value, onChange } }) => (
              <StatusToggleRow
                value={value}
                onChange={onChange}
                helper="Inactive items won't appear in issuance or position mappings."
              />
            )}
          />
        </div>
      </div>
    </FormModalShell>
  );
};

export default UniformItemFormModal;
